import React, { useState, useEffect } from 'react';
import theme from '../config/theme';
import { courseVideos, Video } from '../data/courseVideos';

// Add shimmer animation style
const shimmerStyle = `
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

interface CourseContentProps {
  courseId: string;
  courseName: string;
  user: {
    name: string;
    email: string;
    picture?: string;
  };
  onSignOut: () => void;
  onBackToCourses: () => void;
}

const CourseContent: React.FC<CourseContentProps> = ({
  courseId,
  courseName,
  user,
  onSignOut,
  onBackToCourses
}) => {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [completedVideos, setCompletedVideos] = useState<Set<string>>(new Set());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCompleteButtonHovered, setIsCompleteButtonHovered] = useState(false);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    // Load theme preference from localStorage or default to 'dark'
    const saved = localStorage.getItem('courseContentTheme');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  // Scroll to top when component mounts or courseId changes
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [courseId]);

  // Load completed videos from localStorage on component mount
  React.useEffect(() => {
    const savedCompleted = localStorage.getItem(`completedVideos_${courseId}`);

    if (savedCompleted) {
      try {
        const completedArray = JSON.parse(savedCompleted);
        setCompletedVideos(new Set(completedArray));
      } catch (error) {
        console.error('Error loading completed videos:', error);
      }
    }

    // Mark initial load as complete
    setIsInitialLoad(false);
  }, [courseId]);

  // Handle responsive behavior
  React.useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Save completed videos to localStorage whenever it changes (but not on initial load)
  React.useEffect(() => {
    if (!isInitialLoad) {
      const completedArray = Array.from(completedVideos);
      localStorage.setItem(`completedVideos_${courseId}`, JSON.stringify(completedArray));
    }
  }, [completedVideos, courseId, isInitialLoad]);

  // Course video data - you can expand this for each course
  // Imported from ../data/courseVideos

  const videos = courseVideos[courseId] || [];
  const filteredVideos = videos.filter(v =>
    v.title.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  // Set first video as selected by default
  useEffect(() => {
    if (videos.length > 0 && !selectedVideo) {
      setSelectedVideo(videos[0]);
    }
  }, [videos, selectedVideo]);

  const handleVideoSelect = (video: Video) => {
    setSelectedVideo(video);
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  };

  const handleVideoComplete = (videoId: string) => {
    setCompletedVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  // Debug function to clear completed videos
  const clearCompletedVideos = () => {
    setCompletedVideos(new Set());
    localStorage.removeItem(`completedVideos_${courseId}`);
  };

  const getCourseColor = (courseId: string) => {
    // Use lighter indigo gradient colors for all courses
    return '#818CF8'; // Indigo-400
  };

  const courseColor = getCourseColor(courseId);

  // Light theme color palette
  const lightTheme = {
    bg: '#F9FAFB', // Light gray background
    sidebar: '#FFFFFF', // White sidebar
    card: '#FFFFFF', // White card background
    text: '#111827', // Dark text
    textSecondary: '#6B7280', // Gray text
    textMuted: '#9CA3AF', // Muted gray text
    border: '#E5E7EB', // Light border
    selected: '#6366F1', // Indigo-500 for selected items (same for both)
    selectedBg: '#EEF2FF', // Light indigo background for selected lesson
    hover: '#F3F4F6', // Light hover state
    searchBg: '#F9FAFB', // Light search input background
    progressBg: '#E5E7EB', // Light progress bar background
    progressFill: '#6366F1', // Indigo progress bar fill
  };

  // Dark theme color palette
  const darkTheme = {
    bg: '#0F172A', // Dark slate background
    sidebar: '#1F2937', // Dark gray sidebar
    card: '#111827', // Dark card background
    text: '#F9FAFB', // White text
    textSecondary: '#9CA3AF', // Light gray text
    textMuted: '#6B7280', // Muted gray text
    border: '#374151', // Dark border
    selected: '#6366F1', // Indigo-500 for selected items
    selectedBg: '#6366F1', // Vibrant indigo background for selected lesson
    hover: '#374151', // Hover state
    searchBg: '#374151', // Search input background
    progressBg: '#374151', // Progress bar background
    progressFill: '#6366F1', // Progress bar fill
  };

  // Select current theme based on themeMode
  const currentTheme = themeMode === 'light' ? lightTheme : darkTheme;

  // Save theme preference to localStorage
  React.useEffect(() => {
    localStorage.setItem('courseContentTheme', themeMode);
  }, [themeMode]);

  // Toggle theme function
  const toggleTheme = () => {
    setThemeMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <>
      <style>{shimmerStyle}</style>
      <div style={{
        display: 'flex',
        height: 'auto',
        minHeight: '100vh',
        background: currentTheme.bg,
        transition: 'background-color 0.3s ease',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: 'relative',
        padding: isMobile ? '16px' : '20px',
        paddingBottom: '96px',
        gap: isMobile ? 0 : '20px'
      }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <button
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            style={{
              position: 'fixed',
              top: '90px',
              left: '16px',
              zIndex: 999,
              background: themeMode === 'dark' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(8px)',
              color: currentTheme.text,
              border: `1px solid ${currentTheme.border}`,
              borderRadius: '12px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.background = themeMode === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.background = themeMode === 'dark' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
            Lessons
          </button>
        )}

        {/* Mobile Overlay */}
        {isMobile && isMobileSidebarOpen && (
          <div
            onClick={() => setIsMobileSidebarOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 999,
              backdropFilter: 'blur(2px)',
              transition: 'opacity 0.3s ease'
            }}
          />
        )}

        {/* Left Sidebar - Video List */}
        <div style={{
          width: isMobile ? '320px' : '400px',
          background: currentTheme.sidebar,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? 0 : 'auto',
          left: isMobile ? (isMobileSidebarOpen ? '16px' : '-100%') : 'auto',
          height: isMobile ? 'calc(100vh - 32px)' : 'calc(100vh - 40px)',
          zIndex: isMobile ? 1000 : 'auto',
          transition: isMobile ? 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'all 0.3s ease',
          borderRadius: isMobile ? '12px' : '16px',
          boxShadow: isMobile
            ? (themeMode === 'dark'
              ? '4px 0 32px rgba(0, 0, 0, 0.3), 0 4px 24px rgba(0, 0, 0, 0.2)'
              : '4px 0 32px rgba(0, 0, 0, 0.1), 0 4px 24px rgba(0, 0, 0, 0.08)')
            : (themeMode === 'dark'
              ? '0 4px 24px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)'
              : '0 4px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)'),
          border: `1px solid ${currentTheme.border}`
        }}>
          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: `1px solid ${currentTheme.border}`,
            background: themeMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            color: currentTheme.text,
            transition: 'all 0.3s ease',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              {isMobile ? (
                <button
                  onClick={() => setIsMobileSidebarOpen(false)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'transparent',
                    border: 'none',
                    color: currentTheme.text,
                    padding: '8px 0',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 600,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Close Menu
                </button>
              ) : (
                <button
                  onClick={onBackToCourses}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'transparent',
                    border: 'none',
                    color: currentTheme.textSecondary,
                    padding: '8px 0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    opacity: 0.8
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = currentTheme.selected;
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'translateX(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = currentTheme.textSecondary;
                    e.currentTarget.style.opacity = '0.8';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Back to Course details
                </button>
              )}

              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  border: 'none',
                  color: currentTheme.text,
                  padding: '8px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  width: '32px',
                  height: '32px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'rotate(15deg) scale(1.1)';
                  e.currentTarget.style.background = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'rotate(0) scale(1)';
                  e.currentTarget.style.background = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                }}
                title={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {themeMode === 'light' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                  </svg>
                )}
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: currentTheme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Course Progress
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: currentTheme.selected }}>
                  {Math.round((completedVideos.size / (videos.length || 1)) * 100)}%
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                borderRadius: '999px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${videos.length ? (completedVideos.size / videos.length) * 100 : 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #6366F1 0%, #818CF8 100%)',
                  borderRadius: '999px',
                  transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 0 8px rgba(99, 102, 241, 0.3)'
                }} />
              </div>
              <p style={{ margin: 0, fontSize: '11px', color: currentTheme.textMuted, textAlign: 'right' }}>
                {completedVideos.size} of {videos.length} lessons completed
              </p>
            </div>
          </div>

          {/* Search */}
          <div style={{
            padding: isMobile ? '16px' : '20px',
            background: currentTheme.sidebar,
            borderBottom: `1px solid ${currentTheme.border}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: currentTheme.searchBg,
              border: `1px solid ${currentTheme.border}`,
              borderRadius: '8px',
              padding: '10px 14px',
              transition: 'all 0.2s ease'
            }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = currentTheme.selected;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = currentTheme.border;
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill={currentTheme.textSecondary} style={{ flexShrink: 0, opacity: 0.7 }}>
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zM9.5 14C7.02 14 5 11.98 5 9.5S7.02 5 9.5 5 14 7.02 14 9.5 11.98 14 9.5 14z" />
              </svg>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search lessons..."
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: '13px',
                  color: currentTheme.text,
                  fontWeight: 400
                }}
              />
            </div>
          </div>

          {/* Video List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: isMobile ? '12px 0' : '16px 0',
            scrollbarWidth: 'thin',
            scrollbarColor: `${currentTheme.border} transparent`
          }}>
            {filteredVideos.map((video, index) => (
              <div
                key={video.id}
                onClick={() => handleVideoSelect(video)}
                style={{
                  padding: isMobile ? '14px 16px' : '16px 20px',
                  cursor: 'pointer',
                  borderBottom: `1px solid ${currentTheme.border}`,
                  background: selectedVideo?.id === video.id
                    ? currentTheme.selectedBg
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (selectedVideo?.id !== video.id) {
                    e.currentTarget.style.background = currentTheme.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedVideo?.id !== video.id) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: completedVideos.has(video.id)
                      ? '#10B981'
                      : selectedVideo?.id === video.id
                        ? (themeMode === 'dark' ? 'white' : currentTheme.selected)
                        : currentTheme.hover,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: completedVideos.has(video.id)
                      ? 'white'
                      : selectedVideo?.id === video.id
                        ? (themeMode === 'dark' ? currentTheme.selectedBg : 'white')
                        : currentTheme.textSecondary,
                    fontSize: '14px',
                    fontWeight: 600,
                    flexShrink: 0,
                    transition: 'all 0.2s ease'
                  }}>
                    {completedVideos.has(video.id) ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
                      </svg>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: selectedVideo?.id === video.id ? 700 : 600,
                        color: selectedVideo?.id === video.id
                          ? (themeMode === 'dark' ? 'white' : currentTheme.selected)
                          : currentTheme.text,
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0
                      }}>
                        {video.title}
                      </h4>
                      {video.instructionsPath && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '9px',
                          fontWeight: '600',
                          color: selectedVideo?.id === video.id ? 'white' : currentTheme.selected,
                          backgroundColor: selectedVideo?.id === video.id ? 'rgba(255,255,255,0.2)' : 'rgba(99, 102, 241, 0.15)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          flexShrink: 0
                        }} title="Has step-by-step instructions">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                          </svg>
                          Guide
                        </span>
                      )}
                    </div>
                    {/* Truncated description under title */}
                    {video.description && video.description.trim() !== '...' && (
                      <p style={{
                        margin: '0 0 6px 0',
                        fontSize: '11px',
                        color: selectedVideo?.id === video.id
                          ? (themeMode === 'dark' ? 'rgba(255,255,255,0.7)' : currentTheme.textMuted)
                          : currentTheme.textMuted,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        whiteSpace: 'pre-line'
                      }}>
                        {video.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '10px',
                        color: selectedVideo?.id === video.id
                          ? (themeMode === 'dark' ? 'rgba(255,255,255,0.8)' : currentTheme.textMuted)
                          : currentTheme.textMuted,
                        backgroundColor: selectedVideo?.id === video.id
                          ? (themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(99, 102, 241, 0.1)')
                          : currentTheme.hover,
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontWeight: 500
                      }}>
                        {video.duration}
                      </span>
                      {completedVideos.has(video.id) && (
                        <span style={{
                          fontSize: '10px',
                          color: '#10B981',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
                          </svg>
                          Completed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filteredVideos.length === 0 && (
              <div style={{
                padding: '40px 22px',
                color: currentTheme.textMuted,
                fontSize: '14px',
                textAlign: 'center'
              }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px', opacity: 0.5 }}>
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zM9.5 14C7.02 14 5 11.98 5 9.5S7.02 5 9.5 5 14 7.02 14 9.5 11.98 14 9.5 14z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
                <p style={{ margin: 0, color: currentTheme.textMuted }}>No lessons found</p>
              </div>
            )}
          </div>

        </div>

        {/* Right Side - Video Player */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: isMobile ? 0 : 0,
          width: isMobile ? '100%' : 'auto',
          paddingTop: isMobile ? '60px' : 0,
          paddingBottom: '24px',
          minHeight: 0,
          background: currentTheme.card,
          borderRadius: '16px',
          boxShadow: themeMode === 'dark'
            ? '0 4px 24px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)'
            : '0 4px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${currentTheme.border}`,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
          overflow: 'auto',
          height: isMobile ? 'auto' : 'calc(100vh - 40px)',
          maxHeight: isMobile ? 'none' : 'calc(100vh - 40px)'
        }}>
          {selectedVideo ? (
            <>
              {/* Video Header */}
              <div style={{
                padding: isMobile ? '20px 16px' : '24px 28px',
                backgroundColor: currentTheme.card,
                transition: 'background-color 0.3s ease',
                borderBottom: `1px solid ${currentTheme.border}`
              }}>
                <div style={{
                  margin: '0 0 8px 0',
                  color: currentTheme.textMuted,
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  {courseName}
                </div>
                <h1 style={{
                  margin: '0 0 20px 0',
                  fontSize: isMobile ? '1.75rem' : '2rem',
                  fontWeight: '700',
                  color: currentTheme.text,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em'
                }}>
                  {selectedVideo.title}
                </h1>

                {selectedVideo.description && selectedVideo.description.trim() !== '...' && (
                  <div style={{
                    marginBottom: '20px'
                  }}>
                    {selectedVideo.description
                      .split('\n')
                      .map(line => line.trim())
                      .filter(Boolean)
                      .map((line, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            padding: '8px 0'
                          }}
                        >
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: currentTheme.selected,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '2px',
                              flexShrink: 0
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                              <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
                            </svg>
                          </div>
                          {(() => {
                            const urlMatch = line.match(/https?:\/\/[^^\s]+/i);
                            if (urlMatch) {
                              const url = urlMatch[0];
                              return (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: currentTheme.selected,
                                    fontSize: '14px',
                                    lineHeight: 1.6,
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = 'underline';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = 'none';
                                  }}
                                >
                                  {url}
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3z" />
                                  </svg>
                                </a>
                              );
                            }
                            return <span style={{ color: currentTheme.textSecondary, fontSize: '14px', lineHeight: 1.6, fontWeight: 400 }}>{line}</span>;
                          })()}
                        </div>
                      ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  {selectedVideo.instructionsPath && (
                    <a
                      href={`#/lesson/${courseId}/${selectedVideo.instructionsPath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: currentTheme.selected,
                        fontSize: '13px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        padding: '8px 14px',
                        backgroundColor: themeMode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.08)',
                        borderRadius: '8px',
                        border: `1px solid ${currentTheme.border}`,
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.2)';
                        e.currentTarget.style.borderColor = currentTheme.selected;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                        e.currentTarget.style.borderColor = currentTheme.border;
                      }}
                      title="Open step-by-step instructions in a new tab"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                      </svg>
                      Step-by-Step Guide
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3z" />
                      </svg>
                    </a>
                  )}
                  <button
                    onClick={() => handleVideoComplete(selectedVideo.id)}
                    title={completedVideos.has(selectedVideo.id) ? 'Click to mark as incomplete' : 'Click to mark as complete'}
                    onMouseEnter={() => setIsCompleteButtonHovered(true)}
                    onMouseLeave={() => setIsCompleteButtonHovered(false)}
                    style={{
                      padding: '8px 16px',
                      background: completedVideos.has(selectedVideo.id)
                        ? (isCompleteButtonHovered ? '#EF4444' : '#10B981')
                        : currentTheme.selected,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: isCompleteButtonHovered ? 0.9 : 1
                    }}
                  >
                    {completedVideos.has(selectedVideo.id) ? (
                      <>
                        {isCompleteButtonHovered ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
                          </svg>
                        )}
                        <span>{isCompleteButtonHovered ? 'Mark Incomplete' : 'Completed'}</span>
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.9 }}>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                          <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <span>Mark Complete</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Video Content Area */}
              <div style={{
                flex: 1,
                padding: isMobile ? '16px' : '24px',
                paddingBottom: isMobile ? '40px' : '48px',
                backgroundColor: currentTheme.card,
                transition: 'background-color 0.3s ease',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{
                  backgroundColor: currentTheme.card,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  border: `1px solid ${currentTheme.border}`,
                  transition: 'background-color 0.3s ease, border-color 0.3s ease',
                  position: 'relative',
                  width: '100%'
                }}>
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '56.25%',
                    height: 0,
                    backgroundColor: '#000'
                  }}>
                    <iframe
                      src={`https://player.vimeo.com/video/${selectedVideo.vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479`}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                      title={selectedVideo.title}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 24px',
              color: currentTheme.textMuted
            }}>
              <div style={{ textAlign: 'center', maxWidth: '400px' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 24px',
                  borderRadius: '50%',
                  background: currentTheme.hover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={currentTheme.textMuted} strokeWidth="1.5">
                    <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: currentTheme.text
                }}>
                  No video selected
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  color: currentTheme.textMuted,
                  lineHeight: 1.6
                }}>
                  Choose a video from the list to start learning
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Helper function to get the intro video for a course
export const getCourseIntroVideo = (courseId: string): Video | null => {
  // This will be populated when we move courseVideos outside the component
  return null;
};

export default CourseContent;
